/**
 * Capabilities tab (spec sections 17, 18, 37, 59).
 *
 * There is no single "rally score" here and there never will be: a strong Bear
 * lead and a strong PvP garrison are different players, so scores are per role
 * (spec section 37). Every score shows its confidence and opens to its
 * component breakdown, and a score built mostly from proxy metrics rather than
 * observed events says so.
 */

import type { PlayerCapabilitiesResponse } from '@kso/schemas';
import { usePlayerCapabilities } from '../../../api/queries';

type PlayerCapability = PlayerCapabilitiesResponse['capabilities'][number];
import { Card, WarningList } from '../../../components/ui/primitives';
import { EmptyState, QueryBoundary } from '../../../components/ui/states';
import {
  ConfidenceBadge,
  ExplainToggle,
  InferenceMark,
  SampleCount,
  ScoreBreakdown,
} from '../../../components/inference/Inference';
import { formatDateTime } from '../../../lib/format';
import { useT } from '../../../i18n';

export function CapabilitiesTab({ playerId }: { playerId: string }) {
  const capabilities = usePlayerCapabilities(playerId);
  const t = useT();

  return (
    <QueryBoundary
      query={capabilities}
      loadingLabel={t.capabilities.loading}
      isEmpty={(data) => data.capabilities.length === 0}
      empty={<EmptyState title={t.capabilities.emptyTitle} body={t.capabilities.emptyBody} />}
    >
      {(data) => (
        <div className="stack">
          <p className="muted small" style={{ margin: 0 }}>
            <InferenceMark modelVersion={data.modelVersion} /> {t.capabilities.blurb}
            {data.sourceDataAt
              ? t.capabilities.computedFrom(formatDateTime(data.sourceDataAt) ?? '')
              : ''}
          </p>

          <div className="grid grid--halves">
            {data.capabilities.map((capability) => (
              <CapabilityCard key={capability.role} capability={capability} />
            ))}
          </div>
        </div>
      )}
    </QueryBoundary>
  );
}

function CapabilityCard({ capability }: { capability: PlayerCapability }) {
  const t = useT();
  return (
    <Card
      // `label` verbatim, never derived from `role`. Capability roles are an
      // open set -- BEAR_LEAD and BEAR_JOIN are not RALLY_ROLES members -- so
      // the server is the only thing that knows how to name one.
      title={capability.label || capability.role}
      actions={
        <ConfidenceBadge level={capability.confidenceLevel} confidence={capability.confidence} />
      }
    >
      <div className="row" style={{ alignItems: 'baseline' }}>
        <span className="stat__value">{capability.score.toFixed(0)}</span>
        <SampleCount samples={capability.samples} noun={t.inference.nouns.observations} />
      </div>

      <div style={{ marginTop: 8 }}>
        <WarningList warnings={capability.warnings} />
      </div>

      <div style={{ marginTop: 8 }}>
        <ExplainToggle label={t.capabilities.whatThisScoreIsMadeOf}>
          <ScoreBreakdown components={capability.components} />
        </ExplainToggle>
      </div>
    </Card>
  );
}
